import {
  FunctionComponent,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  MinimalLoader,
  Translation,
  usePlatformConfig,
} from '@dxp-module/common'
import clsx from 'clsx'
import { Alert, Box, Card, CardContent, Grid } from '@mui/material'
import Form, { AjvError, IChangeEvent, ISubmitEvent } from '@rjsf/core'
import { useCustomErrorMessagesMapper } from '../../hooks/useCustomErrorMessagesMapper'
import useRegisterForm from '../../hooks/useRegisterForm'
import useSignupFormValidate from '../../hooks/useSignupFormValidate'
import { SignupFormSchema } from '../../model/SignupForm'
import ButtonSubmit, {
  ButtonSubmitClasses,
} from '../formInput/buttonSubmit/ButtonSubmit'
import useSignupFormSubmit from '../../hooks/useSignupFormSubmit'
import CustomComponentsMapper from '../../../common/components/form/CustomComponentsMapper'
import ObjectFieldTemplate from '../../../common/components/form/objectFieldTemplate/ObjectFieldTemplate'
import FieldTemplate from '../../../common/components/form/fieldTemplate/FieldTemplate'
import SignInUpLink from '../../../common/components/signupLink/SignInUpLink'
import ROUTES from '../../../common/enum/Routes'
import signupStyles from './signup.styles'
import Header from './Header'
import { SignupClassesType } from './SignupContainer'

type SignupProps = {
  classes?: SignupClassesType
  onRegisterSuccess?: (
    success?: boolean | undefined,
    formData?: SignupFormSchema
  ) => void
  buttonSubmitClasses?: ButtonSubmitClasses
  withoutRouter?: boolean
}

const Signup: FunctionComponent<SignupProps> = ({
  classes,
  onRegisterSuccess,
  buttonSubmitClasses,
  withoutRouter,
}) => {
  const [liveValidate, setLiveValidate] = useState<boolean>(false)
  const defaultErrorsRef = useRef<AjvError[] | null>(null)
  const customWidgets = useMemo(() => CustomComponentsMapper(), [])
  const {
    registerForm: { schema, uiSchema, formData },
    requiredFields,
    handleFormChange,
  } = useRegisterForm()
  const {
    platformConfig: {
      myAccount: { isSeparateSignupLogin },
    },
  } = usePlatformConfig()

  const {
    hasErrors,
    asyncErrors,
    resetAsyncErrors,
    handleValidationErrors,
    handleAsyncValidationErrors,
    hasUniqueEmailError,
  } = useSignupFormValidate(requiredFields)
  const style = signupStyles()

  const { onSubmit, isSubmitting } = useSignupFormSubmit()

  const customErrorMessageMapper =
    useCustomErrorMessagesMapper<SignupFormSchema>(formData as SignupFormSchema)

  const handleSubmit = useCallback((event: ISubmitEvent<SignupFormSchema>) => {
    const { formData } = event
    onSubmit(formData).then(register => {
      if (register.errors) {
        return handleAsyncValidationErrors(register.errors)
      }

      return (
        onRegisterSuccess &&
        onRegisterSuccess(register.data?.authentication?.register, formData)
      )
    })
  }, [])

  const onChange = useCallback(
    (data: IChangeEvent<SignupFormSchema>) => {
      if (asyncErrors !== null) {
        resetAsyncErrors()
        data.errors = []
      }
      setLiveValidate(true)
      handleFormChange(data.formData)
      handleValidationErrors(data)
    },
    [asyncErrors, requiredFields]
  )

  const transformErrorsHandler = useCallback(
    (errors: AjvError[]) => {
      defaultErrorsRef.current = errors
      return customErrorMessageMapper(errors)
    },
    [formData]
  )

  if (!formData || !schema || !uiSchema) {
    return <MinimalLoader />
  }

  const isButtonDisabled =
    hasErrors || isSubmitting || !!defaultErrorsRef.current?.length

  return (
    <Grid
      item
      xs={12}
      md={6}
      data-test="my-account:signup"
      className={clsx([style.root, classes?.root])}
    >
      <Card className={clsx([style.paper, classes?.paper])}>
        <CardContent
          className={clsx([style.cardContent, classes?.cardContent])}
        >
          <Header />
          <Form<SignupFormSchema>
            schema={schema}
            uiSchema={uiSchema}
            formData={formData || undefined}
            onSubmit={handleSubmit}
            onChange={onChange}
            liveValidate={liveValidate}
            showErrorList={false}
            transformErrors={transformErrorsHandler}
            widgets={customWidgets}
            extraErrors={asyncErrors}
            ObjectFieldTemplate={ObjectFieldTemplate}
            FieldTemplate={FieldTemplate}
            className={style.form}
          >
            <Box>
              {hasUniqueEmailError && (
                <Alert severity="error" data-test="signup:error:email-exists">
                  <Translation id="trans__myaccount__errors__email_address_exists" />
                </Alert>
              )}
              <ButtonSubmit
                classes={buttonSubmitClasses}
                disabled={isButtonDisabled}
              />
            </Box>
          </Form>
          {withoutRouter || !isSeparateSignupLogin ? null : (
            <SignInUpLink
              textId="trans__myaccount__create_an_account__existing_customer_label"
              linkId="trans__myaccount__create_an_account__existing_customer_cta"
              path={ROUTES.MYACCOUNT_LOGIN}
              testId="my-account:loginLink"
            />
          )}
        </CardContent>
      </Card>
    </Grid>
  )
}

export default Signup
